import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LazyReportSection } from '../hooks/useLazySection';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import type { BingDateGrain } from '../api/bingDashboard';
import { tiktokDashboardApi, type TikTokReportParams, type TikTokReportRow, type TikTokReportType } from '../api/tiktokDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { MultiSelect } from '../components/MultiSelect';
import { ZeeloolFacebookAdFilterSelect } from '../components/ZeeloolFacebookAdFilterSelect';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { SortableHeader, compareSortValues, type SortState } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { DimensionHeaderTooltip, TruncatedValueTooltip } from '../components/DimensionHeaderTooltip';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

interface MetricField {
  key: string;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

interface DimensionField {
  key: string;
  label: string;
}

interface FilterState {
  target_type: string[];
  new_old_type: string[];
  ad_form_type: string[];
  bid_type: string[];
  material_type: string[];
  positioning_type: string[];
  directory_type: string[];
  customer_type: string[];
  price_type: string[];
  audience_type: string[];
  loading_page_type: string[];
  product_type: string[];
  new_customer_segment: string[];
  new_customer_segment_detail: string[];
  old_customer_segment: string[];
  old_customer_segment_detail: string[];
  account_name: string[];
  campaign_id: string;
  campaign_name: string;
  adset_name: string;
  ad_name: string;
}

interface AppliedTopState {
  startDate: string;
  endDate: string;
  dateGrain: BingDateGrain;
}

interface SectionDef {
  report: TikTokReportType;
  title: string;
  dims: string[];
  hasDetailTabs?: boolean;
}

interface TreeMetricAccumulator {
  budget_base: number;
  budget_share: number;
  spend: number;
  payments_complete_value: number;
  impressions: number;
  clicks: number;
  add_to_cart: number;
  checkout_count: number;
  payments_total: number;
  registration: number;
}

interface TreeNode {
  id: string;
  depth: number;
  dimKey: string;
  value: string;
  metrics: TreeMetricAccumulator;
  children: TreeNode[];
  childMap: Map<string, TreeNode>;
}

const SUPPORTED_REGIONS = ['us', 'de'];

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const DIMENSIONS: DimensionField[] = [
  { key: 'date', label: '日期维度' },
  { key: 'target_type', label: '目标' },
  { key: 'new_old_type', label: '新老客' },
  { key: 'ad_form_type', label: '广告形式类型' },
  { key: 'bid_type', label: '出价方式' },
  { key: 'material_type', label: '素材类型' },
  { key: 'positioning_type', label: '定位类型' },
  { key: 'directory_type', label: '目录类型' },
  { key: 'customer_type', label: '客户类型' },
  { key: 'price_type', label: '价格带' },
  { key: 'audience_type', label: '受众人群' },
  { key: 'loading_page_type', label: '着陆页' },
  { key: 'product_type', label: '新品' },
  { key: 'old_customer_segment', label: '老客分层' },
  { key: 'old_customer_segment_detail', label: '老客分层明细' },
  { key: 'new_customer_segment', label: '首单分层' },
  { key: 'new_customer_segment_detail', label: '首单分层明细' },
  { key: 'account_name', label: '广告账户' },
  { key: 'campaign_id', label: '广告系列id' },
  { key: 'campaign_name', label: '广告系列名称' },
  { key: 'adset_name', label: '广告组名称' },
  { key: 'ad_name', label: '广告名称' },
];

const DETAIL_PRESETS: Array<{ value: string; label: string; dims: string[]; report: TikTokReportType }> = [
  { value: 'main', label: '明细报表', dims: ['date'], report: 'detail' },
  { value: 'date', label: '明细报表_日期', dims: ['date'], report: 'detail' },
];

const SECTIONS: SectionDef[] = [
  { report: 'overview', title: '总览报表', dims: ['date'] },
  { report: 'target', title: '目标报表', dims: ['target_type', 'date'] },
  { report: 'new_old', title: '新老客报表', dims: ['new_old_type', 'date'] },
  { report: 'detail', title: '明细报表', dims: DETAIL_PRESETS[0].dims, hasDetailTabs: true },
];

const EMPTY_FILTERS: FilterState = {
  target_type: [],
  new_old_type: [],
  ad_form_type: [],
  bid_type: [],
  material_type: [],
  positioning_type: [],
  directory_type: [],
  customer_type: [],
  price_type: [],
  audience_type: [],
  loading_page_type: [],
  product_type: [],
  new_customer_segment: [],
  new_customer_segment_detail: [],
  old_customer_segment: [],
  old_customer_segment_detail: [],
  account_name: [],
  campaign_id: '',
  campaign_name: '',
  adset_name: '',
  ad_name: '',
};

type EnumFilterKey = {
  [K in keyof FilterState]: FilterState[K] extends string[] ? K : never;
}[keyof FilterState];

type TextFilterKey = {
  [K in keyof FilterState]: FilterState[K] extends string ? K : never;
}[keyof FilterState];

const DETAIL_FILTER_OPTIONS: Array<{ key: EnumFilterKey; label: string; options: string[]; width?: number }> = [
  { key: 'target_type', label: '目标', options: ['上层', '下层', '中层'] },
  { key: 'new_old_type', label: '新老客', options: ['新客', '老客'] },
  { key: 'bid_type', label: '出价方式', options: ['CPA', 'roas', '价值', '其他', '转化'] },
  { key: 'ad_form_type', label: '广告形式类型', options: ['搜索', 'smart+', '其他', 'smart+2.0'] },
  { key: 'material_type', label: '素材类型', options: ['smart+cv', '主页spark', '红人spark-smart+', '红人spark-非smart+', '自建视频-cv广告', '自建视频-非cv', '其他'], width: 180 },
  { key: 'positioning_type', label: '定位类型', options: ['有兴趣受众', '无兴趣受众', '其他'] },
  { key: 'directory_type', label: '目录类型', options: ['所有目录', '常青款目录', '优秀目录', '其他'] },
  { key: 'customer_type', label: '客户类型', options: ['首单', '小排', '排客户列表', '排小排首单老客', '其他'], width: 170 },
  { key: 'price_type', label: '价格带', options: ['7美金', 'sale', '正价', '原价', '其他'] },
  { key: 'audience_type', label: '受众人群', options: ['九大人群', '眼镜包', '其他'] },
  { key: 'loading_page_type', label: '着陆页', options: ['116列表', '273列表', '274列表', '428列表', '455列表', 'SPU搜索页', '专题页', '详情页', '其他', '117列表', '378列表'], width: 180 },
  { key: 'product_type', label: '新品', options: ['当月新品', '非当月新品', '其他'] },
  { key: 'new_customer_segment', label: '首单分层', options: ['未知'] },
  { key: 'new_customer_segment_detail', label: '首单分层明细', options: ['未知'], width: 220 },
  { key: 'old_customer_segment', label: '老客分层', options: ['往年老复购-2026', '其他', '未知'] },
  { key: 'old_customer_segment_detail', label: '老客分层明细', options: ['往年老复购-2026-其他', '未知'], width: 250 },
  { key: 'account_name', label: '广告账户', options: [], width: 220 },
];

const DETAIL_TEXT_FILTERS: Array<{ key: TextFilterKey; label: string; width?: number }> = [
  { key: 'campaign_id', label: '广告系列id', width: 190 },
  { key: 'campaign_name', label: '广告系列名称', width: 220 },
  { key: 'adset_name', label: '广告组名称', width: 220 },
  { key: 'ad_name', label: '广告名称', width: 220 },
];

const UNSUPPORTED_FIELDS_BY_REGION: Partial<Record<string, readonly string[]>> = {
  de: ['material_type', 'positioning_type', 'directory_type', 'customer_type', 'price_type', 'audience_type', 'loading_page_type', 'product_type', 'new_customer_segment', 'new_customer_segment_detail', 'old_customer_segment', 'old_customer_segment_detail', 'campaign_id', 'adset_name', 'ad_name'],
};

const fmtNum = (v: number, d = 2) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`);

const METRICS: MetricField[] = [
  { key: 'budget_share', label: '预算占比', fmt: fmtPct },
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'cpm', label: '账户CPM', fmt: fmtNum, permKey: 'cpc' },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum, permKey: 'cpc' },
  { key: 'spend', label: '账户花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'add_to_cart', label: '加购次数', fmt: fmtNum },
  { key: 'cost_per_add_to_cart', label: '单次加购费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'click_add_to_cart_rate', label: '点击加购率', fmt: fmtPct },
  { key: 'checkout_count', label: '发起结账次数', fmt: fmtInt },
  { key: 'cost_per_checkout', label: '单次结账费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'click_checkout_rate', label: '点击结账率', fmt: fmtPct },
  { key: 'payments_total', label: '购物次数', fmt: fmtNum },
  { key: 'payments_complete_value', label: '购物转化价值', fmt: fmtNum, permKey: 'revenue' },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum, permKey: 'cpa' },
  { key: 'account_roi', label: '账户ROI', fmt: fmtNum, permKey: 'roas' },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'aov', label: '账户单均价', fmt: fmtNum, permKey: 'revenue' },
  { key: 'registration', label: '完成注册次数', fmt: fmtInt },
  { key: 'cost_per_registration', label: '单次注册费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'click_registration_rate', label: '点击注册率', fmt: fmtPct },
  { key: 'purchase_conversion_rate', label: '购物车转化率', fmt: fmtPct },
];

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function getPresetRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function initialAppliedTopState(): AppliedTopState {
  return {
    startDate: daysAgo(6),
    endDate: today(),
    dateGrain: 'day',
  };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function cleanParams<T extends Record<string, unknown>>(params: T): T {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value != null) out[key] = value;
  });
  return out as T;
}

function dimLabel(key: string) {
  return DIMENSIONS.find(d => d.key === key)?.label ?? key;
}

function metricAccumulator(): TreeMetricAccumulator {
  return {
    budget_base: 0,
    budget_share: 0,
    spend: 0,
    payments_complete_value: 0,
    impressions: 0,
    clicks: 0,
    add_to_cart: 0,
    checkout_count: 0,
    payments_total: 0,
    registration: 0,
  };
}

function addMetrics(target: TreeMetricAccumulator, source: Record<string, number> = {}) {
  target.budget_base += source.budget_base ?? 0;
  target.budget_share += source.budget_share ?? 0;
  target.spend += source.spend ?? 0;
  target.payments_complete_value += source.payments_complete_value ?? 0;
  target.impressions += source.impressions ?? 0;
  target.clicks += source.clicks ?? 0;
  target.add_to_cart += source.add_to_cart ?? 0;
  target.checkout_count += source.checkout_count ?? 0;
  target.payments_total += source.payments_total ?? 0;
  target.registration += source.registration ?? 0;
}

function metricFromAccumulator(acc: TreeMetricAccumulator, key: string, totalSpend = acc.budget_base) {
  switch (key) {
    case 'budget_share':
      return totalSpend ? acc.spend / totalSpend : acc.budget_share;
    case 'cpm':
      return acc.impressions ? (acc.spend / acc.impressions) * 1000 : 0;
    case 'account_roi':
      return acc.spend ? acc.payments_complete_value / acc.spend : 0;
    case 'cpa':
      return acc.payments_total ? acc.spend / acc.payments_total : 0;
    case 'aov':
      return acc.payments_total ? acc.payments_complete_value / acc.payments_total : 0;
    case 'ctr':
      return acc.impressions ? acc.clicks / acc.impressions : 0;
    case 'cpc':
      return acc.clicks ? acc.spend / acc.clicks : 0;
    case 'click_purchase_rate':
      return acc.clicks ? acc.payments_total / acc.clicks : 0;
    case 'cost_per_add_to_cart':
      return acc.add_to_cart ? acc.spend / acc.add_to_cart : 0;
    case 'click_add_to_cart_rate':
      return acc.clicks ? acc.add_to_cart / acc.clicks : 0;
    case 'cost_per_checkout':
      return acc.checkout_count ? acc.spend / acc.checkout_count : 0;
    case 'click_checkout_rate':
      return acc.clicks ? acc.checkout_count / acc.clicks : 0;
    case 'cost_per_registration':
      return acc.registration ? acc.spend / acc.registration : 0;
    case 'click_registration_rate':
      return acc.clicks ? acc.registration / acc.clicks : 0;
    case 'purchase_conversion_rate':
      return acc.add_to_cart ? acc.payments_total / acc.add_to_cart : 0;
    default:
      return acc[key as keyof TreeMetricAccumulator] ?? 0;
  }
}

function rowToAccumulator(row: TikTokReportRow | null): TreeMetricAccumulator {
  const acc = metricAccumulator();
  if (row) addMetrics(acc, row.metrics);
  return acc;
}

function buildTree(
  rows: TikTokReportRow[],
  dims: string[],
  subtotalRows: TikTokReportRow[] = [],
) {
  const root: TreeNode = { id: 'root', depth: -1, dimKey: '', value: '', metrics: metricAccumulator(), children: [], childMap: new Map() };
  rows.forEach(row => {
    addMetrics(root.metrics, row.metrics);
    let parent = root;
    dims.forEach((dim, depth) => {
      const value = row.dimensions?.[dim] || '-';
      const id = `${parent.id}/${dim}:${value}`;
      let node = parent.childMap.get(id);
      if (!node) {
        node = { id, depth, dimKey: dim, value, metrics: metricAccumulator(), children: [], childMap: new Map() };
        parent.childMap.set(id, node);
        parent.children.push(node);
      }
      addMetrics(node.metrics, row.metrics);
      parent = node;
    });
  });

  // BI grouping sets recalculate non-additive metrics (especially budget share)
  // for the first-level subtotal. Summing date-level denominators produces a
  // different value, so replace each top node with the matching server subtotal.
  if (dims.length > 1 && subtotalRows.length > 0) {
    const firstDim = dims[0];
    const subtotalByValue = new Map(
      subtotalRows.map(row => [row.dimensions?.[firstDim] || '-', rowToAccumulator(row)]),
    );
    root.children.forEach(node => {
      const subtotal = subtotalByValue.get(node.value);
      if (subtotal) node.metrics = subtotal;
    });
  }
  return root.children;
}

function visibleTreeRows(nodes: TreeNode[], expanded: Set<string>) {
  const out: TreeNode[] = [];
  const walk = (items: TreeNode[]) => {
    items.forEach(node => {
      out.push(node);
      if (node.children.length > 0 && expanded.has(node.id)) walk(node.children);
    });
  };
  walk(nodes);
  return out;
}

function expandableTreeNodeIDs(nodes: TreeNode[]) {
  const ids: string[] = [];
  const walk = (items: TreeNode[]) => items.forEach(node => {
    if (node.children.length > 0) ids.push(node.id);
    walk(node.children);
  });
  walk(nodes);
  return ids;
}

/** Sort metric values within each group while retaining the complete report tree. */
function sortTreeSiblings(nodes: TreeNode[], sort: SortState, userLevel: number): TreeNode[] {
  if (!sort) return nodes;
  const metric = METRICS.find(item => item.key === sort.key);
  // Sorting a masked metric would reveal its relative raw values.
  if (!metric || (metric.permKey && userLevel < 2)) return nodes;

  const ordered = nodes
    .map((node, index) => ({ node, index }))
    .sort((left, right) => {
      const result = compareSortValues(
        metricFromAccumulator(left.node.metrics, metric.key),
        metricFromAccumulator(right.node.metrics, metric.key),
        sort.direction,
      );
      return result === 0 ? left.index - right.index : result;
    });

  return ordered.map(({ node }) => (
    node.children.length === 0
      ? node
      : { ...node, children: sortTreeSiblings(node.children, sort, userLevel) }
  ));
}

function detailReportForDims(_dims: string[]): TikTokReportType {
  return 'detail';
}

function normalizeDetailDims(dims: string[]) {
  return dims.length > 0 ? dims : ['date'];
}

function EnumFilter({
  label,
  field,
  fallbackOptions,
  value,
  onChange,
  width = 170,
}: {
  label: string;
  field: string;
  fallbackOptions: string[];
  value: string[];
  onChange: (v: string[]) => void;
  width?: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: width }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</label>
      <ReportLabelSelect dataset="tiktok_delivery" field={field} pageFields={DETAIL_FILTER_OPTIONS.map(item => item.key)} fallbackOptions={fallbackOptions} value={value} onChange={onChange} width={width} />
    </div>
  );
}

function serializeFilters(filters: FilterState) {
  const out: Record<string, string> = {};
  (Object.keys(filters) as Array<keyof FilterState>).forEach(key => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) out[key] = value.join(',');
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  });
  return out;
}

function DimensionPicker({ selected, onChange, dimensions = DIMENSIONS }: { selected: string[]; onChange: (next: string[]) => void; dimensions?: DimensionField[] }) {
  return (
    <div className="dimension-select-row" style={{ display: 'flex' }}>
      <span className="dimension-select-label">动态维度</span>
      <MultiSelect
        options={dimensions.map(dim => ({ value: dim.key, label: dim.label }))}
        value={dimensions.filter(dim => selected.includes(dim.key)).map(dim => dim.key)}
        onChange={onChange}
        countLabelFormatter={count => `已选 ${count} 项`}
        tagDisplay
        style={{ width: 420 }}
      />
    </div>
  );
}

function TikTokSectionTable({
  section,
  baseParams,
  queryVersion,
  hasPendingTopChanges,
  onQueryAll,
  userLevel,
  region,
}: {
  section: SectionDef;
  baseParams: Omit<TikTokReportParams, 'report' | 'dimensions'> | null;
  queryVersion: number;
  hasPendingTopChanges: boolean;
  onQueryAll: () => void;
  userLevel: number;
  region: string;
}) {
  const [detailPreset, setDetailPreset] = useState('main');
  const [dims, setDims] = useState<string[]>(section.dims);
  const [detailReport, setDetailReport] = useState<TikTokReportType>(section.report);
  const [appliedDims, setAppliedDims] = useState<string[]>(section.dims);
  const [appliedDetailReport, setAppliedDetailReport] = useState<TikTokReportType>(section.report);
  const [draftFilters, setDraftFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [appliedTopQueryVersion, setAppliedTopQueryVersion] = useState(queryVersion);
  const [detailQueryVersion, setDetailQueryVersion] = useState(0);
  const [rows, setRows] = useState<TikTokReportRow[]>([]);
  const [subtotalRows, setSubtotalRows] = useState<TikTokReportRow[]>([]);
  const [summary, setSummary] = useState<TikTokReportRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportQuery, setExportQuery] = useState<TikTokReportParams | null>(null);
  const [exportDims, setExportDims] = useState<string[]>(section.dims);
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>(null);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`tiktok_dashboard_hidden_cols_${section.report}`);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore corrupted local preference
    }
    return new Set();
  });

  const activeDims = section.hasDetailTabs ? appliedDims : section.dims;
  const hasExpandControls = section.report !== 'overview';
  const treeRoots = useMemo(
    () => buildTree(rows, activeDims, subtotalRows),
    [activeDims, rows, subtotalRows],
  );
  const sortedTreeRoots = useMemo(() => sortTreeSiblings(treeRoots, sort, userLevel), [sort, treeRoots, userLevel]);
  const visibleRows = useMemo(() => visibleTreeRows(sortedTreeRoots, expanded), [expanded, sortedTreeRoots]);
  const expandableNodeIDs = useMemo(() => expandableTreeNodeIDs(treeRoots), [treeRoots]);
  const availableMetrics = useMemo(
    () => METRICS.filter(metric => region !== 'de' || metric.key !== 'budget_share'),
    [region],
  );
  const visibleMetrics = useMemo(
    () => availableMetrics.filter(metric => !hiddenMetricKeys.has(metric.key)),
    [availableMetrics, hiddenMetricKeys],
  );
  const metricToggleCols = useMemo(
    () => availableMetrics.map(metric => ({ key: metric.key, label: metric.label })),
    [availableMetrics],
  );
  const unsupportedFieldKeys = UNSUPPORTED_FIELDS_BY_REGION[region] ?? [];
  const availableDetailFilterOptions = useMemo(
    () => DETAIL_FILTER_OPTIONS.filter(item => !unsupportedFieldKeys.includes(item.key)),
    [unsupportedFieldKeys],
  );
  const availableDetailTextFilters = useMemo(
    () => DETAIL_TEXT_FILTERS.filter(item => !unsupportedFieldKeys.includes(item.key)),
    [unsupportedFieldKeys],
  );
  const availableDimensions = useMemo(
    () => DIMENSIONS.filter(item => !unsupportedFieldKeys.includes(item.key)),
    [unsupportedFieldKeys],
  );

  const toggleSort = useCallback((key: string) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);
  const currentReportParams = useMemo(() => {
    if (!baseParams) return null;
    return cleanParams({
      ...baseParams,
      ...(section.hasDetailTabs ? serializeFilters(appliedFilters) : {}),
      report: section.hasDetailTabs ? appliedDetailReport : section.report,
      dimensions: activeDims.join(','),
    });
  }, [activeDims, appliedDetailReport, appliedFilters, baseParams, section.hasDetailTabs, section.report]);

  const toggleMetricHidden = (key: string) => {
    setSort(current => current?.key === key ? null : current);
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(`tiktok_dashboard_hidden_cols_${section.report}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const resetMetricHidden = () => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(`tiktok_dashboard_hidden_cols_${section.report}`); } catch {}
  };

  const fetchData = useCallback(async () => {
    if (!currentReportParams) {
      setExportQuery(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const shouldLoadSubtotal = !section.hasDetailTabs && activeDims.length > 1;
      const [res, subtotalRes] = await Promise.all([
        tiktokDashboardApi.getReport(currentReportParams),
        shouldLoadSubtotal
          ? tiktokDashboardApi.getReport({ ...currentReportParams, dimensions: activeDims[0] })
          : Promise.resolve(null),
      ]);
      setRows(res.data?.list ?? []);
      setSubtotalRows(subtotalRes?.data?.list ?? []);
      setSummary(res.data?.summary ?? null);
      setExpanded(new Set());
      setExportQuery(currentReportParams);
      setExportDims(activeDims);
    } catch (e) {
      setRows([]);
      setSubtotalRows([]);
      setSummary(null);
      setExportQuery(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [activeDims, currentReportParams, section.hasDetailTabs]);

  useEffect(() => {
    if (appliedTopQueryVersion === queryVersion) return;
    setAppliedDims(dims);
    setAppliedDetailReport(detailReport);
    setAppliedFilters(draftFilters);
    setAppliedTopQueryVersion(queryVersion);
  }, [appliedTopQueryVersion, detailReport, dims, draftFilters, queryVersion]);

  useEffect(() => {
    if (appliedTopQueryVersion !== queryVersion) return;
    fetchData();
  }, [appliedTopQueryVersion, detailQueryVersion, fetchData, queryVersion]);

  const setEnumDetailFilter = (key: EnumFilterKey, value: string[]) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  const setTextDetailFilter = (key: TextFilterKey, value: string) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  useMarketingViewSection('detail:' + section.report, {
    label: section.title, state: { detailPreset, dims, detailReport, draftFilters, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setDetailPreset(value.detailPreset); setDims(value.dims); setDetailReport(value.detailReport); setDraftFilters(value.draftFilters); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: loading,
  });

  const queryDetail = () => {
    setAppliedFilters(draftFilters);
    setAppliedDims(dims);
    setAppliedDetailReport(detailReport);
    if (hasPendingTopChanges) onQueryAll();
    else setDetailQueryVersion(version => version + 1);
  };
  const resetDetail = () => {
    setDetailPreset('main');
    setDims(section.dims); setAppliedDims(section.dims);
    setDetailReport(section.report); setAppliedDetailReport(section.report);
    setDraftFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS);
    setDetailQueryVersion(version => version + 1);
  };
  const applyDetailDimensions = (next: string[]) => {
    const normalized = normalizeDetailDims(next);
    const nextReport = detailReportForDims(normalized);
    setDims(normalized); setAppliedDims(normalized);
    setDetailReport(nextReport); setAppliedDetailReport(nextReport);
  };
  const renderMetric = (acc: TreeMetricAccumulator, metric: MetricField) => {
    const raw = metric.fmt(metricFromAccumulator(acc, metric.key));
    if (!metric.permKey) return raw;
    return sf(raw, metric.permKey, userLevel).value ?? '锁定';
  };

  const renderSummaryMetric = (metric: MetricField) => {
    const acc = rowToAccumulator(summary);
    return renderMetric(acc, metric);
  };

  const handleExport = useCallback(async () => {
    if (!exportQuery) return;
    try {
      await enqueueReportExport({
        report_key: 'ad_dashboard_tiktok_report',
        title: `TikTok投放看板-${section.title}`,
        file_name: `TikTok投放看板_${section.title}_${exportQuery.start_date}_${exportQuery.end_date}.csv`,
        brand_id: exportQuery.brand_id,
        site_id: exportQuery.site_id,
        params: { ...exportQuery },
        columns: [
          ...exportDims.map(dim => ({ key: dim, label: dimLabel(dim) })),
          ...visibleMetrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '导出任务提交失败', 'error');
    }
  }, [enqueueReportExport, exportDims, exportQuery, section.title, showToast, visibleMetrics]);

  const toggleNode = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(expandableNodeIDs));
  const collapseAll = () => setExpanded(new Set());

  return (
    <section style={{
      marginTop: 16,
      background: 'var(--surface)',
      border: '1px solid var(--border2)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(15,23,42,.04)',
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{section.title}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {section.report !== 'overview' && <>
            <button type="button" className="btn btn-outline btn-sm" onClick={expandAll} disabled={loading || expandableNodeIDs.length === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Maximize2 size={14} strokeWidth={2} />全部展开</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={collapseAll} disabled={loading || expanded.size === 0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Minimize2 size={14} strokeWidth={2} />全部收起</button>
          </>}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleExport}
            disabled={loading || !exportQuery}
            title="导出当前报表"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={14} strokeWidth={2} />
            导出
          </button>
          <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
        </div>
      </div>

      {section.hasDetailTabs && (
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, borderBottom: '1px solid var(--border2)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            {availableDetailFilterOptions.map(item => (
              <EnumFilter
                key={item.key}
                label={item.label}
                field={item.key}
                fallbackOptions={item.options}
                value={draftFilters[item.key]}
                width={item.width}
                onChange={v => setEnumDetailFilter(item.key, v)}
              />
            ))}
            {availableDetailTextFilters.map(item => (
              <ZeeloolFacebookAdFilterSelect
                key={item.key}
                label={item.label}
                field={item.key}
                platform="tiktok"
                value={draftFilters[item.key]}
                width={item.width}
                onConfirm={v => setTextDetailFilter(item.key, v)}
              />
            ))}
            <DashboardQueryActions loading={loading} onQuery={queryDetail} onReset={resetDetail} queryLabel="查询" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="page-tabs" style={{ marginBottom: 0 }}>
              {DETAIL_PRESETS.map(item => (
                <div
                  key={item.value}
                  className={`page-tab${detailPreset === item.value ? ' active' : ''}`}
                  onClick={() => {
                    setDetailPreset(item.value);
                    applyDetailDimensions(item.dims);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
            <DimensionPicker
              selected={dims}
              dimensions={availableDimensions}
              onChange={next => {
                applyDetailDimensions(next);
              }}
            />
          </div>
        </div>
      )}

      {error && <div style={{ margin: '12px 16px 0', color: '#dc2626', fontSize: 13 }}>{error}</div>}

      <div className="table-wrap" style={{ position: 'relative', maxHeight: section.hasDetailTabs ? 620 : 420, overflow: 'auto', borderTop: section.hasDetailTabs ? 0 : '1px solid var(--border2)' }}>
        <table style={{ minWidth: Math.max(1280, activeDims.length * 120 + visibleMetrics.length * 95) }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
            <tr>
              <th style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', width: '1%', minWidth: 160, maxWidth: 360, boxShadow: '2px 0 4px rgba(15,23,42,.06)' } : { whiteSpace: 'nowrap', background: 'var(--surface2)', minWidth: 240 }}>
                {hasExpandControls ? <DimensionHeaderTooltip labels={(section.hasDetailTabs ? DIMENSIONS.map(dim => dim.key) : activeDims).map(dim => dimLabel(dim))} /> : '汇总'}
              </th>
              {visibleMetrics.map(metric => (
                <SortableHeader
                  key={metric.key}
                  sortKey={metric.key}
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                  disabled={Boolean(metric.permKey && userLevel < 2)}
                  style={{ whiteSpace: 'nowrap', background: 'var(--surface2)' }}
                >
                  <MetricDescriptionTooltip label={metric.label} />
                </SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary && (
              <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
                <td style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', whiteSpace: 'nowrap', color: 'var(--text2)' } : { whiteSpace: 'nowrap', color: 'var(--text2)' }}>总计</td>
                {visibleMetrics.map(metric => (
                  <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderSummaryMetric(metric)}</td>
                ))}
              </tr>
            )}
            {loading && visibleRows.length === 0 ? (
              <tr><td colSpan={99}><TableLoadingCell /></td></tr>
            ) : !baseParams ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>请在左侧切换到支持的 TikTok 站点</td></tr>
            ) : visibleRows.length === 0 ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>暂无数据</td></tr>
            ) : visibleRows.map((node, index) => (
              <tr key={node.id} className={node.children.length > 0 ? 'tree-row-parent' : undefined}>
                <td style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 1, background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', width: '1%', minWidth: 160, maxWidth: 360 } : { minWidth: 240, whiteSpace: node.dimKey === 'campaign_name' ? 'normal' : 'nowrap' }}>
                  <span className="tree-cell-content" style={{ paddingLeft: node.depth * 20 }}>
                    {node.children.length > 0 && (
                      <button type="button" className="tree-toggle-btn" onClick={() => toggleNode(node.id)} aria-label={expanded.has(node.id) ? '收起' : '展开'} aria-expanded={expanded.has(node.id)}>
                        {expanded.has(node.id) ? '−' : '+'}
                      </button>
                    )}
                    {(node.children.length > 0 || node.depth > 0) && <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 'var(--radius-xs)', background: 'var(--primary-bg)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{dimLabel(node.dimKey).replace(/维度$/, '')}</span>}
                    {hasExpandControls ? <TruncatedValueTooltip value={node.value} /> : <span>{node.value}</span>}
                  </span>
                </td>
                {visibleMetrics.map(metric => (
                  <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {renderMetric(node.metrics, metric)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && visibleRows.length > 0 && <TableLoadingOverlay />}
      </div>
    </section>
  );
}

export default function TiktokDeliveryDashboard() {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedTop, setAppliedTop] = useState<AppliedTopState>(() => initialAppliedTopState());
  const [queryVersion, setQueryVersion] = useState(0);
  const initialQueryKeyRef = useRef('');

  const currentSite = useMemo(() => {
    if (brand !== 'zeelool') return null;
    const site = sites.find(item => item.id === siteId);
    if (!site) return null;
    const region = regionFromSiteCode(site.code);
    if (!SUPPORTED_REGIONS.includes(region)) return null;
    return { region, siteId: site.id, siteCode: site.code };
  }, [brand, siteId, sites]);

  const dateRange = useMemo(() => alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const baseParams = useMemo(() => {
    if (!brandId || !currentSite?.siteId) return null;
    return cleanParams({
      brand_id: brandId,
      site_id: currentSite.siteId,
      start_date: appliedTop.startDate,
      end_date: appliedTop.endDate,
      date_grain: appliedTop.dateGrain,
    });
  }, [appliedTop, brandId, currentSite?.siteId]);
  const hasPendingTopChanges = dateRange.start !== appliedTop.startDate
    || dateRange.end !== appliedTop.endDate
    || dateGrain !== appliedTop.dateGrain;

  const query = () => {
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
    setQueryVersion(v => v + 1);
  };
  useMarketingViewSection('page', {
    label: '公共日期', state: { dateGrain, datePreset, customStart, customEnd },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); },
    loading: false, query: () => { query(); },
    resolve: value => { const range = getPresetRange(value.datePreset, value.customStart, value.customEnd); return { ...value, customStart: range.start, customEnd: range.end }; },
    summary: dateRange.start + ' ～ ' + dateRange.end,
  });

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(daysAgo(7));
    setCustomEnd(today());
    const range = getPresetRange('7d', daysAgo(7), today());
    setAppliedTop({ startDate: range.start, endDate: range.end, dateGrain: 'day' });
    setQueryVersion(v => v + 1);
  };
  useEffect(() => {
    if (!brandId || !currentSite?.siteId) return;
    const key = `${brandId}:${currentSite.siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
    setQueryVersion(v => v + 1);
  }, [brandId, currentSite?.siteId, dateGrain, dateRange.end, dateRange.start]);

  if (!sitesLoaded) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 0 }}><TableLoadingState /></div>
      </div>
    );
  }

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter grain={dateGrain} grains={DATE_GRAINS} onGrainChange={setDateGrain} preset={datePreset} presets={DELIVERY_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
          <DashboardQueryActions onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {!currentSite && (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
          请在左侧切换到 US / DE 站点后查看 TikTok 投放看板。
        </div>
      )}

      {currentSite && SECTIONS.map(section => (
        <LazyReportSection key={section.report} enabled={Boolean(currentSite)}>
          <TikTokSectionTable
            section={section}
            baseParams={baseParams}
            queryVersion={queryVersion}
            hasPendingTopChanges={hasPendingTopChanges}
            onQueryAll={query}
            userLevel={userLevel}
            region={currentSite.region}
          />
        </LazyReportSection>
      ))}
    </div>
  );
}
